import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Send, Loader, ArrowRight, Zap, Code2, ShoppingCart, MessageSquare, CheckSquare, FileText, Server, Bot, Sparkles, CreditCard, BarChart3 } from 'lucide-react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TEMPLATES = [
  { label: 'SaaS dashboard', icon: Code2, idea: 'Build a SaaS dashboard with user analytics, subscription management, and team collaboration features' },
  { label: 'E-commerce store', icon: ShoppingCart, idea: 'Build an e-commerce store with product catalog, cart, checkout, and order management' },
  { label: 'Chat application', icon: MessageSquare, idea: 'Build a real-time chat application with rooms, direct messages, and file sharing' },
  { label: 'Task manager', icon: CheckSquare, idea: 'Build a task management app with boards, lists, drag-and-drop, and team assignments' },
  { label: 'Blog platform', icon: FileText, idea: 'Build a blog platform with rich text editor, categories, comments, and SEO optimization' },
  { label: 'API gateway', icon: Server, idea: 'Build an API gateway with rate limiting, authentication, logging, and request routing' },
];

export default function BuildPage() {
  const navigate = useNavigate();
  const { credits, refreshCredits } = useAuth();
  const [idea, setIdea] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recentProjects, setRecentProjects] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [labsInfo, setLabsInfo] = useState({ is_student: false, is_admin: false });
  const [mode, setMode] = useState('professional');  // "student" | "professional"
  const estimateTimerRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/projects`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setRecentProjects(data.slice(0, 5)); })
      .catch(() => {});
    // Pull labs flags so we can force Student Mode for whitelisted students
    // and show the admin link to admins.
    fetch(`${API_URL}/api/labs/whoami`, { credentials: 'include' })
      .then(r => r.json())
      .then(info => {
        setLabsInfo(info || {});
        // Only auto-select Student Mode when the legacy 3-agent workflow is
        // still enabled on the backend. With the flag OFF (default from
        // 2026-02 on) students belong on /labs, not here.
        if (info?.is_student && info?.student_workflow_enabled) setMode('student');
      })
      .catch(() => {});
  }, []);

  const deriveProjectName = (text) => {
    const words = text.trim().split(/\s+/).slice(0, 5);
    if (words.length === 0) return 'New Project';
    return words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  // Debounced cost estimate as user types
  const fetchEstimate = async (text) => {
    if (text.trim().length < 10) { setEstimate(null); return; }
    setEstimateLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/estimate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea: text.trim() }),
      });
      if (res.ok) setEstimate(await res.json());
    } catch (e) { /* silent */ }
    setEstimateLoading(false);
  };

  const handleIdeaChange = (val) => {
    setIdea(val);
    if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    estimateTimerRef.current = setTimeout(() => fetchEstimate(val), 800);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!idea.trim()) return;
    setError('');
    setLoading(true);
    try {
      const projectName = deriveProjectName(idea);
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: projectName, idea: idea.trim(), description: '', mode }),
        credentials: 'include',
      });
      if (response.status === 402) {
        const data = await response.json();
        setError(data.detail || 'Not enough credits');
        return;
      }
      if (!response.ok) throw new Error('Failed to create project');
      const project = await response.json();
      refreshCredits();
      navigate(`/project/${project.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template) => {
    handleIdeaChange(template.idea);
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  };

  const getStatusColor = (status) => {
    if (status === 'building' || status === 'running') return '#6366f1';
    if (status === 'self_fixing') return '#a78bfa';
    if (status === 'waiting_for_user') return '#f59e0b';
    if (status === 'failed') return '#ef4444';
    return '#10b981';
  };

  return (
    <div data-testid="build-page" style={{
      display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)',
      background: 'linear-gradient(180deg, hsl(var(--background)) 0%, hsl(240 20% 98%) 100%)',
      position: 'relative',
    }}>
      {/* Subtle gradient orb background */}
      <div style={{
        position: 'absolute', top: '-10%', right: '-5%', width: '500px', height: '500px',
        background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-10%', left: '-5%', width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(139,92,246,0.04) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />
      {/* Main content — centered */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: '720px', margin: '0 auto', width: '100%', padding: '2rem 1rem', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '1rem', margin: '0 auto 1.25rem',
            background: '#0f172a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 1px rgba(99,102,241,0.2), 0 8px 24px rgba(0,0,0,0.12)',
          }}>
            <Bot size={26} style={{ color: '#818cf8' }} />
          </div>
          <h1 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em',
            marginBottom: '0.5rem', lineHeight: 1.15,
          }}>
            What do you want to build?
          </h1>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.9375rem', maxWidth: '480px', margin: '0 auto' }}>
            Describe your idea and CodeFoundry will design, code, test, and deploy it — with your approval at every stage.
          </p>
        </div>

        {/* Chat-style input card */}
        <div style={{
          background: 'hsl(var(--card))',
          border: '1px solid hsl(var(--border))',
          borderRadius: '0.875rem',
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          transition: 'box-shadow 0.2s',
          marginBottom: '1.5rem',
        }}>
          <form onSubmit={handleSubmit}>
            {/* Student / Professional mode toggle. Legacy — only rendered
                when STUDENT_WORKFLOW_ENABLED=true on the backend. The flag
                defaults OFF because labs now use the single-call sidebar
                in /playground?lab=N (see services/lab_prompts.py). */}
            {labsInfo.student_workflow_enabled && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.75rem 1.25rem 0', fontSize: '0.8125rem',
            }} data-testid="mode-toggle">
              <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 500 }}>Mode:</span>
              <button
                type="button"
                onClick={() => setMode('student')}
                data-testid="mode-student-btn"
                style={{
                  padding: '0.3rem 0.75rem', borderRadius: '0.5rem',
                  border: mode === 'student' ? '1px solid hsl(var(--primary))' : '1px solid hsl(var(--border))',
                  background: mode === 'student' ? 'hsl(var(--primary) / 0.1)' : 'transparent',
                  color: mode === 'student' ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
                  fontWeight: mode === 'student' ? 600 : 400, cursor: 'pointer', fontSize: '0.8125rem',
                }}
              >Student · 3 agents</button>
              <button
                type="button"
                onClick={() => !labsInfo.is_student && setMode('professional')}
                disabled={labsInfo.is_student}
                data-testid="mode-professional-btn"
                title={labsInfo.is_student ? 'Professional Mode is disabled for whitelisted students' : ''}
                style={{
                  padding: '0.3rem 0.75rem', borderRadius: '0.5rem',
                  border: mode === 'professional' ? '1px solid hsl(var(--primary))' : '1px solid hsl(var(--border))',
                  background: mode === 'professional' ? 'hsl(var(--primary) / 0.1)' : 'transparent',
                  color: labsInfo.is_student ? 'hsl(var(--muted-foreground) / 0.6)' : (mode === 'professional' ? 'hsl(var(--primary))' : 'hsl(var(--foreground))'),
                  fontWeight: mode === 'professional' ? 600 : 400,
                  cursor: labsInfo.is_student ? 'not-allowed' : 'pointer',
                  fontSize: '0.8125rem',
                  opacity: labsInfo.is_student ? 0.5 : 1,
                }}
              >Professional · 16 agents{labsInfo.is_student && ' (locked)'}</button>
              {labsInfo.is_admin && (
                <Link to="/labs/admin" data-testid="labs-admin-link" style={{
                  marginLeft: 'auto', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textDecoration: 'none',
                }}>Manage student whitelist →</Link>
              )}
            </div>
            )}
            {/* Students: redirect to /labs for the single-call lab sidebar. */}
            {!labsInfo.student_workflow_enabled && labsInfo.is_student && (
              <div
                data-testid="student-labs-redirect-banner"
                style={{
                  margin: '0.75rem 1.25rem 0',
                  padding: '0.75rem 0.9rem',
                  borderRadius: '0.5rem',
                  background: 'hsla(238, 83%, 67%, 0.08)',
                  border: '1px solid hsla(238, 83%, 67%, 0.3)',
                  fontSize: '0.8125rem', color: 'hsl(var(--foreground))',
                  display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1 }}>
                  <strong>You're a student — your labs live on the Labs page.</strong>
                  <div style={{ color: 'hsl(var(--muted-foreground))', marginTop: 2, fontSize: '0.75rem' }}>
                    Pick a lab, press <em>Ask the agent</em>, run the code and export your submission — all free.
                  </div>
                </div>
                <Link
                  to="/labs"
                  data-testid="student-labs-redirect-cta"
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '0.4rem',
                    background: 'hsl(var(--primary))',
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: '0.75rem', fontWeight: 600,
                  }}
                >Open Labs →</Link>
              </div>
            )}
            <textarea
              ref={textareaRef}
              placeholder="Build a real-time analytics dashboard with user authentication, data visualization charts, and team management..."
              value={idea}
              onChange={(e) => {
                handleIdeaChange(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && idea.trim()) { e.preventDefault(); handleSubmit(); } }}
              rows={3}
              style={{
                width: '100%', resize: 'none', minHeight: '100px', maxHeight: '200px',
                fontSize: '0.9375rem', padding: '1.25rem 1.25rem 0.75rem',
                border: 'none', background: 'transparent', color: 'hsl(var(--foreground))',
                lineHeight: 1.6, outline: 'none', fontFamily: 'inherit',
              }}
              data-testid="build-idea-textarea"
            />

            {error && (
              <div style={{
                margin: '0 1.25rem', padding: '0.5rem 0.75rem',
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: '0.5rem', color: '#ef4444', fontSize: '0.8125rem',
              }} data-testid="build-error-message">
                {error}
              </div>
            )}

            {/* Upfront Cost Estimate Card */}
            {(estimate || estimateLoading) && idea.trim().length >= 10 && (
              <div data-testid="build-estimate-card" style={{
                margin: '0 1.25rem 0.5rem', padding: '0.75rem 1rem',
                background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)',
                borderRadius: '0.5rem',
              }}>
                {estimateLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Loader size={14} style={{ color: '#818cf8', animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>Estimating build cost...</span>
                  </div>
                ) : estimate && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <BarChart3 size={14} style={{ color: '#818cf8' }} />
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#c7d2fe' }}>Build Estimate</span>
                      </div>
                      <span style={{
                        fontSize: '0.875rem', fontWeight: 700,
                        color: estimate.can_afford ? '#10b981' : '#ef4444',
                      }}>
                        ~{estimate.total_estimated_credits} credits
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.75rem', color: '#94a3b8' }}>
                      <span>Complexity: <strong style={{ color: '#c7d2fe' }}>{estimate.complexity}</strong></span>
                      <span>~{estimate.estimated_files} files</span>
                      <span>~{estimate.estimated_lines?.toLocaleString()} lines</span>
                    </div>
                    {!estimate.can_afford && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <CreditCard size={12} />
                        Need {estimate.credits_needed} more credits.
                        <a href="/buy-credits" style={{ color: '#818cf8', textDecoration: 'underline' }}>Buy Credits</a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.75rem 1.25rem', borderTop: '1px solid hsl(var(--border))',
              background: 'hsl(var(--secondary) / 0.3)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Sparkles size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                  {credits} credits available
                </span>
              </div>
              <button
                type="submit"
                disabled={loading || !idea.trim()}
                style={{
                  padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                  background: (!idea.trim() || loading) ? 'hsl(var(--secondary))' : '#6366f1',
                  color: (!idea.trim() || loading) ? 'hsl(var(--muted-foreground))' : 'white',
                  border: 'none', cursor: loading ? 'wait' : 'pointer',
                  fontWeight: 600, fontSize: '0.8125rem',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  transition: 'all 0.15s',
                }}
                data-testid="build-start-button"
              >
                {loading ? (
                  <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Building...</>
                ) : (
                  <><Send size={15} /> Start Build</>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Template suggestions */}
        <div style={{ marginBottom: '2.5rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Try a template
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            {TEMPLATES.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => handleTemplateClick(t)}
                style={{
                  padding: '0.625rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.75rem',
                  background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
                  color: 'hsl(var(--foreground))', cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  textAlign: 'left', fontWeight: 500,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'hsl(var(--border))'; e.currentTarget.style.background = 'hsl(var(--card))'; }}
                data-testid="build-idea-template-chip"
              >
                <t.icon size={14} style={{ color: '#818cf8', flexShrink: 0 }} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recent projects */}
        {recentProjects.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Recent builds
              </p>
              <Link to="/history" style={{ fontSize: '0.6875rem', color: '#6366f1', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 600 }}>
                View all <ArrowRight size={11} />
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {recentProjects.map((p) => (
                <Link
                  key={p.id}
                  to={`/project/${p.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.625rem 0.875rem', borderRadius: '0.5rem',
                    background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))',
                    textDecoration: 'none', color: 'inherit',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'hsl(var(--muted-foreground))'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'hsl(var(--border))'; }}
                  data-testid="project-row-open-button"
                >
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                    background: getStatusColor(p.status),
                  }} />
                  <span style={{ fontWeight: 600, fontSize: '0.8125rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                    {p.idea?.slice(0, 40)}{p.idea?.length > 40 ? '...' : ''}
                  </span>
                  <ArrowRight size={12} style={{ color: 'hsl(var(--muted-foreground))', flexShrink: 0 }} />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
